/**
 * Moteur déterministe de génération d'instance — le cœur du principe
 * "l'IA construit le Blueprint une fois, le moteur génère ensuite sans
 * nouvel appel IA" (§13 de la spec). Pur, aucun accès réseau/DB : tire des
 * paramètres via un PRNG seedé, vérifie les contraintes, enchaîne les calculs
 * du solve path, rend les textes et produit les blocs QCM/calculatrice.
 *
 * Appelé côté client (admin/tuteur) — le résultat est ensuite écrit comme un
 * exercice V2 normal via `exercise-blocks-mutate` (aucune edge function
 * dédiée à la génération elle-même).
 */

import { evalExpr, toEvalScope, type Scope } from '../shared/expr';
import { callMathFunction } from './math-functions';
import { validateConstraints } from './constraints';
import type {
  BlueprintDistractorRule,
  BlueprintParameter,
  BlueprintSpec,
  BlueprintTemplate,
  BlueprintVisualTemplate,
} from './schemas';

// ── PRNG déterministe (mulberry32 + FNV-1a) ─────────────────────────────────
// Copie volontairement locale et autonome (même algorithme que
// packages/db/src/live/steps.ts, non exporté par ce module) : ~15 lignes
// pures sans dépendance, pour ne pas coupler le Blueprint au Live School.

function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Tirage de paramètres ─────────────────────────────────────────────────────

function drawParameterValue(param: BlueprintParameter, rng: () => number): number {
  const isInteger = param.type === 'integer' || param.type === 'positiveInteger';
  if (isInteger) {
    const min = Math.ceil(param.min);
    const max = Math.floor(param.max);
    return min + Math.floor(rng() * (max - min + 1));
  }
  const decimals = param.decimals ?? 1;
  const scale = 10 ** decimals;
  const raw = param.min + rng() * (param.max - param.min);
  return Math.round(raw * scale) / scale;
}

// ── Rendu de texte ───────────────────────────────────────────────────────────
// Le 1er caractère DOIT être une minuscule (calque exact de BlueprintNameSchema,
// `/^[a-z][a-zA-Z0-9_]*$/`) — sinon `\mathbb{R}$`/`\frac{a}{b}`-style LaTeX
// (très fréquent en maths : `\mathbb{R}`/`{N}`/`{Z}`/`{Q}`/`{C}`…) serait
// confondu avec un placeholder Blueprint et soit rejeté en validation
// ("placeholder inconnu {R}"), soit pire, silencieusement substitué. Un nom
// de paramètre commençant par une majuscule n'existe structurellement jamais
// (BlueprintNameSchema l'interdit) → cette regex ne perd donc aucun vrai
// placeholder, elle élimine seulement les faux positifs LaTeX.

const PLACEHOLDER_REGEX = /\{([a-z][a-zA-Z0-9_]*)\}/g;

/** Évite les artefacts flottants (ex. 0.1+0.2) en formatant à 6 décimales significatives max. */
export function formatNumber(value: number): string {
  const rounded = Math.round(value * 1e6) / 1e6;
  return String(rounded);
}

function renderTemplate(text: string, scope: Scope): string {
  return text.replace(PLACEHOLDER_REGEX, (_match, name: string) => {
    const value = scope[name];
    if (value === undefined) throw new Error(`Placeholder inconnu au rendu : {${name}}`);
    return formatNumber(value);
  });
}

function pickTemplate(
  templates: readonly BlueprintTemplate[],
  rng: () => number,
): BlueprintTemplate {
  const idx = Math.min(Math.floor(rng() * templates.length), templates.length - 1);
  return templates[idx]!;
}

// ── Visuel paramétré (graphique de fonction / tableau) ──────────────────────
// Forme littérale (NON templatée) d'un bloc visuel généré — sous-ensemble
// minimal de la forme runtime attendue par les renderers V2 existants
// (function-graph-block.tsx / table-block.tsx). Pas de dépendance vers
// @jeraisonne/ai depuis packages/db : le mapping vers ExerciseVisualBlock se
// fait côté apps/app (cf. lib/blueprint/generated-visual-to-block.ts).

export interface GeneratedFunctionGraphBlock {
  id: string;
  order: number;
  type: 'function_graph';
  viewport?: { xMin: number; xMax: number; yMin: number; yMax: number };
  functions: { id: string; expression: string; label?: string; color?: string }[];
  points: { id: string; name?: string; x: number; y: number; color?: string; label?: string }[];
}

export interface GeneratedTableBlock {
  id: string;
  order: number;
  type: 'table';
  headers: string[] | null;
  rows: string[][];
}

export type GeneratedVisualBlock = GeneratedFunctionGraphBlock | GeneratedTableBlock;

/** Évalue un "nombre templaté" (littéral, ou expression evalExpr entièrement résolue — cf. schemas.ts). */
function resolveTemplatedNumber(value: number | string, scope: Scope): number {
  return typeof value === 'number' ? value : evalExpr(value, toEvalScope(scope));
}

/** Rend un champ texte optionnel (label/name) — peut contenir des placeholders {nom}, comme text_md. */
function renderOptionalText(text: string | undefined, scope: Scope): string | undefined {
  return text === undefined ? undefined : renderTemplate(text, scope);
}

function renderVisualTemplate(visual: BlueprintVisualTemplate, scope: Scope): GeneratedVisualBlock {
  if (visual.kind === 'table') {
    return {
      id: 'v1',
      order: 0,
      type: 'table',
      headers: visual.headers ? visual.headers.map((h) => renderTemplate(h, scope)) : null,
      rows: visual.rows.map((row) => row.map((cell) => renderTemplate(cell, scope))),
    };
  }

  return {
    id: 'v1',
    order: 0,
    type: 'function_graph',
    viewport: visual.viewport
      ? {
          xMin: resolveTemplatedNumber(visual.viewport.xMin, scope),
          xMax: resolveTemplatedNumber(visual.viewport.xMax, scope),
          yMin: resolveTemplatedNumber(visual.viewport.yMin, scope),
          yMax: resolveTemplatedNumber(visual.viewport.yMax, scope),
        }
      : undefined,
    functions: visual.functions.map((fn) => ({
      id: fn.id,
      expression: renderTemplate(fn.expression, scope),
      label: renderOptionalText(fn.label, scope),
      color: fn.color,
    })),
    points: (visual.points ?? []).map((p) => ({
      id: p.id,
      name: renderOptionalText(p.name, scope),
      x: resolveTemplatedNumber(p.x, scope),
      y: resolveTemplatedNumber(p.y, scope),
      color: p.color,
      label: renderOptionalText(p.label, scope),
    })),
  };
}

/** Rejette un visuel dont un champ numérique n'est pas fini (NaN/Infinity — ex. division par zéro pour ce tirage). */
function isFiniteVisual(visual: GeneratedVisualBlock): boolean {
  if (visual.type === 'table') return true;
  const viewportOk =
    !visual.viewport ||
    [visual.viewport.xMin, visual.viewport.xMax, visual.viewport.yMin, visual.viewport.yMax].every(
      Number.isFinite,
    );
  return viewportOk && visual.points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

// ── Distracteurs déterministes ───────────────────────────────────────────────

interface OriginComputation {
  functionId: string;
  args: readonly string[];
}

function applyDistractorRule(
  rule: BlueprintDistractorRule,
  correctValue: number,
  origin: OriginComputation | undefined,
  scope: Scope,
): number {
  switch (rule.kind) {
    case 'offset':
      return correctValue + rule.amount;
    case 'multiply':
      return correctValue * rule.factor;
    case 'swapArgs': {
      if (!origin) throw new Error('Règle "swapArgs" sans étape de calcul associée à la question');
      const argValues = origin.args.map((expr) => evalExpr(expr, toEvalScope(scope)));
      if (argValues.length < 2) throw new Error('Règle "swapArgs" nécessite au moins 2 arguments');
      const swapped = [argValues[1]!, argValues[0]!, ...argValues.slice(2)];
      return callMathFunction(rule.functionId, swapped);
    }
    case 'formulaRef': {
      if (!origin)
        throw new Error('Règle "formulaRef" sans étape de calcul associée à la question');
      const argValues = origin.args.map((expr) => evalExpr(expr, toEvalScope(scope)));
      return callMathFunction(rule.functionId, argValues);
    }
  }
}

// ── Résultat de génération ───────────────────────────────────────────────────

export interface GeneratedQcmQuestion {
  questionId: string;
  prompt_md: string;
  /** Corrigé rendu (paramétré) de CETTE question — cf. explanationForQuestion. */
  explanation_md: string;
  answerMode: 'qcm';
  correctText_md: string;
  distractors_md: string[];
}

export interface GeneratedCalculatorQuestion {
  questionId: string;
  prompt_md: string;
  /** Corrigé rendu (paramétré) de CETTE question — cf. explanationForQuestion. */
  explanation_md: string;
  answerMode: 'calculator';
  expectedValue: number;
  tolerance: number;
  unitLabel?: string;
  availableVariables: { label: string; value: number }[];
}

export type GeneratedQuestion = GeneratedQcmQuestion | GeneratedCalculatorQuestion;

export interface GeneratedSolveStep {
  /** Id de l'étape dans `spec.solvePath` — permet d'attribuer les étapes à une question (`question.solveStepIds`). */
  id: string;
  label: string;
  text_md: string;
}

/** Met en forme une suite d'étapes rendues en markdown de corrigé. Pur. */
export function formatSolvePathExplanation(steps: readonly GeneratedSolveStep[]): string {
  return steps.map((s) => `**${s.label}.** ${s.text_md}`).join('\n\n');
}

/**
 * Corrigé D'UNE question : les étapes qu'elle déclare (`solveStepIds`), dans
 * l'ordre du solve path. Sans déclaration, tout le solve path — comportement
 * historique de 0221, où chaque correction recopiait l'intégralité du corrigé.
 */
export function explanationForQuestion(
  steps: readonly GeneratedSolveStep[],
  solveStepIds: readonly string[] | undefined,
): string {
  if (!solveStepIds || solveStepIds.length === 0) return formatSolvePathExplanation(steps);
  const wanted = new Set(solveStepIds);
  const kept = steps.filter((s) => wanted.has(s.id));
  return formatSolvePathExplanation(kept.length > 0 ? kept : steps);
}

export interface GeneratedInstance {
  seed: string;
  parameterValues: Record<string, number>;
  results: Record<string, number>;
  statement_md: string;
  solvePathSteps: GeneratedSolveStep[];
  questions: GeneratedQuestion[];
  visual?: GeneratedVisualBlock;
}

export interface GenerateInstanceOptions {
  seed: string;
  /** Nombre maximal de tirages avant d'abandonner (contraintes trop strictes / distracteurs en collision). */
  maxAttempts?: number;
}

const DEFAULT_MAX_ATTEMPTS = 200;

export class GenerateInstanceError extends Error {}

function buildComputationIndex(spec: BlueprintSpec): Map<string, OriginComputation> {
  const index = new Map<string, OriginComputation>();
  for (const step of spec.solvePath) {
    if (step.computation) {
      index.set(step.computation.resultName, {
        functionId: step.computation.functionId,
        args: step.computation.args,
      });
    }
  }
  return index;
}

/** Une tentative de tirage : renvoie `null` si elle doit être abandonnée (contrainte violée, collision, calcul invalide). */
function tryGenerateAttempt(
  spec: BlueprintSpec,
  seed: string,
  attempt: number,
  computationByResult: ReadonlyMap<string, OriginComputation>,
): GeneratedInstance | null {
  const rng = mulberry32(hashSeed(`${seed}:${attempt}`));

  const parameterValues: Record<string, number> = {};
  for (const param of spec.parameters) parameterValues[param.name] = drawParameterValue(param, rng);

  const scope: Scope = { ...parameterValues };
  const solvePathSteps: GeneratedSolveStep[] = [];

  for (const step of spec.solvePath) {
    if (step.computation) {
      let value: number;
      try {
        const argValues = step.computation.args.map((expr) => evalExpr(expr, toEvalScope(scope)));
        value = callMathFunction(step.computation.functionId, argValues);
      } catch {
        return null; // ex. division par zéro pour ce tirage → nouvelle tentative
      }
      if (!Number.isFinite(value)) return null;
      scope[step.computation.resultName] = value;
    }
    solvePathSteps.push({
      id: step.id,
      label: step.label,
      text_md: renderTemplate(step.text_md, scope),
    });
  }

  if (!validateConstraints(spec.constraints, scope)) return null;

  // Le visuel peut référencer un résultat du solve path (cf. validateBlueprintSpec,
  // même raison qu'un texte de solve path peut référencer son propre résultat) —
  // rendu APRÈS la boucle solvePath pour que `scope` contienne tous les résultats.
  let visual: GeneratedVisualBlock | undefined;
  if (spec.visual) {
    try {
      visual = renderVisualTemplate(spec.visual, scope);
    } catch {
      return null; // ex. expression invalide pour ce tirage → nouvelle tentative
    }
    if (!isFiniteVisual(visual)) return null;
  }

  const answeredResults: { label: string; value: number }[] = [];
  const questions: GeneratedQuestion[] = [];
  for (const question of spec.questions) {
    const expected = scope[question.resultName];
    if (expected === undefined) {
      throw new GenerateInstanceError(`Résultat manquant pour la question "${question.id}"`);
    }
    const prompt_md = renderTemplate(question.prompt_md, scope);
    const explanation_md = explanationForQuestion(solvePathSteps, question.solveStepIds);

    if (question.answerMode === 'calculator') {
      const availableVariables = [
        ...spec.parameters.map((p) => ({ label: p.name, value: parameterValues[p.name]! })),
        ...answeredResults,
      ];
      questions.push({
        questionId: question.id,
        prompt_md,
        explanation_md,
        answerMode: 'calculator',
        expectedValue: expected,
        tolerance: question.tolerance!,
        unitLabel: question.unitLabel,
        availableVariables,
      });
    } else {
      const origin = computationByResult.get(question.resultName);
      let distractorValues: number[];
      try {
        distractorValues = (question.distractorRules ?? []).map((rule) =>
          applyDistractorRule(rule, expected, origin, scope),
        );
      } catch {
        return null;
      }
      // Anti-collision : un distracteur ne doit dupliquer ni la bonne réponse ni un autre distracteur.
      const seenTexts = new Set([formatNumber(expected)]);
      for (const value of distractorValues) {
        const text = formatNumber(value);
        if (seenTexts.has(text)) return null;
        seenTexts.add(text);
      }
      questions.push({
        questionId: question.id,
        prompt_md,
        explanation_md,
        answerMode: 'qcm',
        correctText_md: formatNumber(expected),
        distractors_md: distractorValues.map(formatNumber),
      });
    }

    answeredResults.push({ label: question.resultName, value: expected });
  }

  const template = pickTemplate(spec.templates, rng);
  const statement_md = renderTemplate(template.text_md, scope);

  return { seed, parameterValues, results: scope, statement_md, solvePathSteps, questions, visual };
}

/**
 * Génère une instance jouable du Blueprint. Déterministe : la même `seed`
 * produit toujours la même instance (même tirage, mêmes gabarits choisis).
 * Aucun appel IA — uniquement des calculs et de la substitution de texte.
 */
export function generateInstance(
  spec: BlueprintSpec,
  opts: GenerateInstanceOptions,
): GeneratedInstance {
  const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const computationByResult = buildComputationIndex(spec);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = tryGenerateAttempt(spec, opts.seed, attempt, computationByResult);
    if (result) return result;
  }

  throw new GenerateInstanceError(
    `Impossible de générer une instance valide après ${maxAttempts} tentatives (contraintes trop strictes ou règles de distracteurs en collision).`,
  );
}
